import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { Alert } from '@mm/components';
import classnames from 'classnames';
import React, { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import Spinner from '../../components/Spinner';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import './AudioWidget.pcss';
import {
  MAX_AUDIO_SIZE,
  AUDIO_EXTENSIONS,
  acceptAudioExtensionList,
} from './constants';
import { useAudioWidgetHandlers } from '../../helpers/widget-helpers/use-audio-widget-handlers';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const AUDIO_SIZE_CAP = 0.6 * 1024 * 1024;

const useUploadAudio = (widget: WidgetModel) => {
  const audioWidgetHandlers = useAudioWidgetHandlers();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const [isUploading, setIsUploading] = useState(false);

  const uploadAudio = async ([file]: File[], rejectedFiles: File[]) => {
    if (rejectedFiles.length) {
      return dispatch(
        modalActions.openPrettyErrorModal({
          title: 'Unsupported Extension',
          errorMessage: `Supported file extensions are .mp3`,
        }),
      );
    }

    if (file) {
      if (file.size > MAX_AUDIO_SIZE) {
        return dispatch(
          modalActions.openPrettyErrorModal({
            title: 'Upload Size Limit',
            errorMessage: 'The audio should be less than 25MB',
          }),
        );
      }

      try {
        setIsUploading(true);
        await audioWidgetHandlers.addAudioToWidget(widget, file);
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
      }
    }
  };

  return { isUploading, uploadAudio };
};

export const AudioWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    forInstagram,
    isSmsRelated,
    smsOnly,
    platforms,
  } = props;
  const {
    config: { description },
    file,
  } = widget;
  const { isUploading, uploadAudio } = useUploadAudio(widget);
  const { getRootProps, getInputProps } = useDropzone({
    accept: AUDIO_EXTENSIONS,
    onDrop: uploadAudio,
  });

  const { sourceSize } = widget.config;

  const isSizeOvercap =
    isSmsRelated && sourceSize && sourceSize > AUDIO_SIZE_CAP;

  return (
    <div
      className={classnames('audio-widget', {
        'audio-widget--has-file': file,
      })}
    >
      <FacebookPageIcon />

      {file ? (
        <div className="audio-widget__file-area-wrapper">
          {isUploading && (
            <div className="audio-widget__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <audio
            title={description}
            className="audio-widget__audio"
            controls
            src={file}
          />

          <div
            className="audio-widget__file-area-upload-hover"
            {...getRootProps()}
          >
            <input {...getInputProps()} accept={acceptAudioExtensionList} />

            <i className="far fa-file-audio fa-3x audio-widget__upload-icon" />
            <span>Upload new Audio</span>

            <div className="audio-widget__file-area-upload-hover-blinder" />
          </div>
        </div>
      ) : (
        <div className="audio-widget__drop-area" {...getRootProps()}>
          {isUploading && (
            <div className="audio-widget__uploading-wrapper">
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}

          <input {...getInputProps()} accept={acceptAudioExtensionList} />

          <div className="audio-widget__drop-area-text">
            <i className="far fa-file-audio fa-3x audio-widget__upload-icon" />
            <span>
              Drop audio file or
              <span className="audio-widget__choose-file">choose file</span>
            </span>
          </div>
        </div>
      )}
      {isSizeOvercap && (
        <Alert
          className="audio-widget__warning"
          type={smsOnly ? 'error' : 'warning'}
          showCloseIcon={false}
        >
          This media file exceeds size limit on SMS and will NOT be sent in your
          SMS dialogue. Please reduce size to &lt;600KB.
        </Alert>
      )}

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />

      {forInstagram && !widget.supportedByPlatforms.instagram && (
        <ErrorPortal widget={widget}>
          <WidgetUnsupportedError isError type="instagram" />
        </ErrorPortal>
      )}
    </div>
  );
};
